import { ApolloError } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';

import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Loader } from '@/components/progress/Loader/Loader';
import { FormProvider, useFormContext } from '@/components/react-hook-form';
import { useForm, useSubmitSuccessHandler } from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { AddNewAnythingModal } from '@/modules/common/AddNewAnythingModal/AddNewAnythingModal';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { MultiDocumentUploaderProvider } from '@/modules/documents/MultiDocumentUploader/context/MultiDocumentUploader.provider';
import { ENTITY_TYPES } from '@/modules/entities/entities.constants';
import { EntityType } from '@/modules/entities/types/EntityType';
import {
  AugmentedCreateLoggedTransferInput,
  AugmentedUpdateLoggedTransferInput,
  DocumentType,
} from '@/types/schema';

import { NON_GIFT_RECIPIENT_ENTITY_TYPES } from '../gifting.constants';
import { useClientDetailsGiftingPageQuery } from '../hooks/useClientDetailsGiftingPageQuery';
import { useCreateLoggedTransferMutation } from '../LogNewTransferModal/graphql/CreateLoggedTransfer.generated';
import { useUpdateLoggedTransferMutation } from '../LogNewTransferModal/graphql/UpdateLoggedTransfer.generated';
import { useDeleteTransferAndExclusionEventMutation } from './graphql/LogNewGiftModal.generated';
import { getDefaultValues } from './LogNewGiftModal.constants';
import { LogNewGiftModalForm, useFormOptions } from './LogNewGiftModal.form';
import { LogNewGiftForm } from './LogNewGiftModal.types';
import {
  getCreateLoggedTransferEventInput,
  getUpdateLifetimeExclusionEventInput,
  useSyncDataToForm,
} from './LogNewGiftModal.utils';

// The GRAT short form navigates to the GRAT designer, which discards the
// gift in progress.
const GIFT_RECIPIENT_ENTITY_TYPES: EntityType[] = Object.values(
  ENTITY_TYPES
).filter(
  (entityType) =>
    !NON_GIFT_RECIPIENT_ENTITY_TYPES.includes(entityType) &&
    entityType !== ENTITY_TYPES.GRAT
);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  householdId: string;
  lifetimeExclusionEventId?: string;
  beneficiaryId?: string;
  isDuplicate?: boolean;
  isEdit?: boolean;
}

function LogNewGift({
  isOpen,
  onClose,
  householdId,
  lifetimeExclusionEventId,
  isDuplicate,
  isEdit,
  beneficiaryId,
}: Props) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const {
    uploadDocuments,
    setSelectedExistingDocuments,
    selectedExistingDocuments,
  } = useMultiDocumentUploaderContext();
  const { formRef, handleSave } = useFormSaveHandler();
  const trackUserEvent = useTrackUserEvent();
  const canEdit = useCanEditHouseholdData();

  const { grantorOptions, beneficiaryOptions, possibleBeneficiaries } =
    useFormOptions({ householdId });

  const {
    handleSubmit,
    reset,
    shouldBlockNavigation,
    formState,
    getValues,
    setValue,
  } = useFormContext<LogNewGiftForm>();
  const [isAddingRecipient, setIsAddingRecipient] = useState(false);

  const { loading: loadingSyncDataToForm, originalDocumentIds } =
    useSyncDataToForm({
      lifetimeExclusionEventId,
      isDuplicate,
      defaultBeneficiaryId: beneficiaryId,
      isOpen,
    });

  const clientDetailsGiftingPageState =
    useClientDetailsGiftingPageQuery(householdId);

  const loadingForm =
    loadingSyncDataToForm || clientDetailsGiftingPageState.loading;

  // Initialize existing documents in the uploader context
  const originalDocumentIdsArray = originalDocumentIds.current;

  useEffect(() => {
    if (originalDocumentIdsArray.length > 0) {
      setSelectedExistingDocuments(
        originalDocumentIdsArray.map((id) => ({
          id,
          displayName: '', // we don't use them in this context
        }))
      );
    }
    // We intentionally depend on the array reference to ensure this effect
    // fires whenever it is updated inside `useSyncDataToForm`.
  }, [originalDocumentIdsArray, setSelectedExistingDocuments]);

  const [createLoggedTransfer] = useCreateLoggedTransferMutation({
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onError: (error) => {
      showFeedback('Error logging gift, please try again', {
        variant: 'error',
      });
      reportError('could not create lifetime exclusion event', error, {
        householdId,
      });
    },
    onCompleted: () => {
      showFeedback('Gift logged successfully', { variant: 'success' });
    },
  });

  const [updateLoggedTransfer] = useUpdateLoggedTransferMutation({
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onError: (error) => {
      showFeedback('Error logging gift, please try again');
      reportError('could not update lifetime exclusion event', error, {
        householdId,
      });
    },
    onCompleted: () => {
      showFeedback('Gift logged successfully', {
        variant: 'success',
      });
    },
  });

  const onDeleteGiftError = (error: ApolloError) => {
    showFeedback('Error deleting gift, please try again');
    reportError('could not delete lifetime exclusion event', error, {
      householdId,
    });
  };

  const [
    deleteLoggedTransferAndLifetimeExclusionEvent,
    { loading: isDeleting },
  ] = useDeleteTransferAndExclusionEventMutation({
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onError: onDeleteGiftError,
    onCompleted: () =>
      showFeedback('Gift deleted successfully', {
        variant: 'success',
      }),
  });

  const onValidSubmission: SubmitHandler<LogNewGiftForm> = async (values) => {
    try {
      // Upload documents first and get their IDs
      const uploadedDocumentIds = await uploadDocuments({
        householdId,
        defaultDocumentType: DocumentType.GiftTaxReturn,
      });

      // Use the current selected documents from context instead of form values
      const existingDocumentIds = selectedExistingDocuments.map(
        (doc) => doc.id
      );
      const allDocumentIds = [...existingDocumentIds, ...uploadedDocumentIds];
      const updatedValues = {
        ...values,
        logNewGiftModal: {
          ...values.logNewGiftModal,
          documentIds: allDocumentIds,
        },
      };

      if (isEdit) {
        const updateLoggedTransferInput: AugmentedUpdateLoggedTransferInput =
          getUpdateLifetimeExclusionEventInput({
            form: updatedValues,
            originalDocumentIds,
            possibleBeneficiaries,
            householdId,
          });

        // submit update form
        const output = await updateLoggedTransfer({
          variables: {
            input: updateLoggedTransferInput,
          },
        });
        trackUserEvent('gift updated');
        return output;
      } else {
        const createLifetimeExclusionEventInput: AugmentedCreateLoggedTransferInput =
          getCreateLoggedTransferEventInput(
            updatedValues,
            householdId,
            possibleBeneficiaries
          );

        // submit create form
        const output = await createLoggedTransfer({
          variables: {
            input: createLifetimeExclusionEventInput,
          },
        });
        trackUserEvent('gift logged');
        return output;
      }
    } catch (error) {
      reportError('Failed to upload documents', error as Error);
      showFeedback('Failed to upload documents. Please try again.');
      throw error;
    }
  };

  const submitHandler = handleSubmit(onValidSubmission);

  const closeModal = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  useSubmitSuccessHandler(() => {
    closeModal();
  });

  const onDelete = useCallback(async () => {
    const loggedTransferId = getValues('logNewGiftModal.loggedTransferId');
    const lifetimeExclusionEventId = getValues(
      'logNewGiftModal.lifetimeExclusionEventId'
    );

    if (!loggedTransferId || !lifetimeExclusionEventId) {
      showFeedback(
        'Cannot delete gift: missing required information. Please refresh and try again.',
        { variant: 'error' }
      );
      reportError('Missing IDs when attempting to delete gift', null, {
        loggedTransferId,
        lifetimeExclusionEventId,
      });
      return;
    }

    await deleteLoggedTransferAndLifetimeExclusionEvent({
      variables: {
        deleteLoggedTransferId: loggedTransferId,
        deleteLifetimeExclusionEventId: lifetimeExclusionEventId,
      },
    });

    closeModal();
  }, [
    closeModal,
    deleteLoggedTransferAndLifetimeExclusionEvent,
    getValues,
    showFeedback,
    reportError,
  ]);

  let submitButtonText = 'Add gift';

  if (isEdit) {
    submitButtonText = 'Save changes';
  }

  if (isDuplicate) {
    submitButtonText = 'Duplicate gift';
  }

  const actions = (
    <FormModalActions.Provider
      formState={formState}
      isDeleting={isDeleting}
      disabled={loadingSyncDataToForm}
      viewOnly={!canEdit}
    >
      {isEdit && <FormModalActions.DeleteButton onConfirmDelete={onDelete} />}
      <FormModalActions.CancelButton onClick={closeModal} />
      <FormModalActions.SaveButton onClick={handleSave}>
        {submitButtonText}
      </FormModalActions.SaveButton>
    </FormModalActions.Provider>
  );

  const heading = useMemo(() => {
    if (isEdit) {
      return 'Edit gift';
    }

    if (isDuplicate) {
      return 'Duplicate as new gift';
    }

    return 'Add gift';
  }, [isDuplicate, isEdit]);

  return (
    <FormModal
      isOpen={isOpen}
      heading={heading}
      onClose={closeModal}
      actions={actions}
      shouldBlockClose={shouldBlockNavigation}
    >
      {loadingForm ? (
        <Loader
          boxProps={{
            sx: {
              textAlign: 'center',
              my: 3,
            },
          }}
        />
      ) : (
        <HouseholdPermissionAwareDisabledProvider forceDisabled={loadingForm}>
          <form ref={formRef} onSubmit={submitHandler} noValidate>
            <LogNewGiftModalForm
              grantorOptions={grantorOptions}
              beneficiaryOptions={beneficiaryOptions}
              householdId={householdId}
              advisorClient={clientDetailsGiftingPageState.household!}
              onAddNewRecipient={() => setIsAddingRecipient(true)}
            />
          </form>
        </HouseholdPermissionAwareDisabledProvider>
      )}
      {/* Render outside the gift form. React bubbles submit events through
          portals, so a nested creation form would also submit the gift form.
          Keep it mounted: it closes its picker before it opens a create form. */}
      <AddNewAnythingModal
        isOpen={isAddingRecipient}
        handleClose={() => setIsAddingRecipient(false)}
        householdId={householdId}
        addableTypes={['individual', 'organization', 'entity']}
        addEntityModalProps={{ limitEntityTypes: GIFT_RECIPIENT_ENTITY_TYPES }}
        onAfterCreate={(newRecipientId) =>
          setValue(
            'logNewGiftModal.beneficiary.beneficiaryId',
            newRecipientId,
            {
              shouldDirty: true,
              shouldValidate: true,
            }
          )
        }
      />
    </FormModal>
  );
}

export function LogNewGiftModal({ ...props }: Props) {
  const formMethods = useForm<LogNewGiftForm>({
    defaultValues: getDefaultValues({
      defaultBeneficiaryId: props.beneficiaryId,
      isForcedBeneficiary: !!props.beneficiaryId,
    }),
  });

  return (
    <MultiDocumentUploaderProvider>
      <FormProvider {...formMethods}>
        <LogNewGift {...props} />
      </FormProvider>
    </MultiDocumentUploaderProvider>
  );
}
